/**
 * SDK 自绘范围日历(B5)
 *
 * 不用 react-day-picker(重依赖);datebar 按设计不需要套餐级不可选日期规则,
 * 仅 minDate=今天/maxDate=+6 月 + 基础范围选择。
 *
 * 浮层用 absolute 定位在 Shadow DOM 内(见 DateBar),不 Portal 到 body(样式隔离约束)。
 *
 * 桌面:双月并排 + 头部 ‹/› 箭头切月(原样,零影响)。
 * 移动(≤640px):三月 [上月,当月,下月] 竖向轮播,裁剪只露当月,竖向滑动连续切月,
 *   对齐 /hub/packages 页面日历;窄屏 PC(有鼠标)在裁剪窗外浮 ‹/› 自绘箭头。
 *
 * 选区交互心智(简化六分支,无禁用区间/连住规则):
 *   - 无 from 或 from&to 都有:点 d -> 以 d 为新 from(清 to)
 *   - 有 from 无 to:
 *       d == from -> 清空(toggle off)
 *       d < from  -> 以 d 替换 from
 *       d > from  -> 设 to,提交 onChange
 */
import { useState, useMemo, useCallback } from 'react';
import {
  isSameDay,
  differenceInCalendarDays,
  addMonths,
  startOfDay,
  getWeekdayZh,
} from '../lib/date';
import { useMediaQuery } from '@/ui/hooks/useMediaQuery';
import { useVerticalSwipeMonth } from './useVerticalSwipeMonth';

export interface CalendarRange {
  start: Date;
  end: Date;
  nights: number;
}

interface CalendarProps {
  defaultStartDate: Date;
  defaultEndDate: Date;
  minDate: Date;
  maxDate: Date;
  onChange: (range: CalendarRange) => void;
}

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

interface DayCell {
  date: Date;
  inMonth: boolean;
}

/** 构造某月的日历单元格(含前置占位,以周日为一周起点);fixedWeeks 时补足 6 周(42 格)保等高 */
function buildMonthGrid(year: number, month: number, fixedWeeks = false): DayCell[] {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay(); // 0=周日
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: DayCell[] = [];
  for (let i = 0; i < startWeekday; i++) {
    cells.push({ date: new Date(year, month, i - startWeekday + 1), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), inMonth: true });
  }
  if (fixedWeeks) {
    while (cells.length < 42) {
      cells.push({ date: new Date(year, month, cells.length + 1), inMonth: false });
    }
  }
  return cells;
}

function monthLabel(date: Date): string {
  return `${date.getFullYear()}年${date.getMonth() + 1}月`;
}

export function Calendar({
  defaultStartDate,
  defaultEndDate,
  minDate,
  maxDate,
  onChange,
}: CalendarProps) {
  // 移动端判定与页面同源(纯 useSyncExternalStore hook,无重依赖)。断点 640px 与 styles.h5.ts 一致。
  const isMobile = useMediaQuery('(max-width: 640px)', false);
  // 窄屏 PC(宽<640 但有鼠标、无触摸)下显示浮箭头,纯触摸机型不渲染(仅滑动)。
  const hasFinePointer = useMediaQuery('(any-pointer: fine)', true);

  const [from, setFrom] = useState<Date | undefined>(startOfDay(defaultStartDate));
  const [to, setTo] = useState<Date | undefined>(startOfDay(defaultEndDate));
  // leftMonth 即"当前显示月":桌面为左月,移动为轮播中间月。
  const [leftMonth, setLeftMonth] = useState<Date>(() => {
    const d = startOfDay(defaultStartDate);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const minD = startOfDay(minDate);
  const maxD = startOfDay(maxDate);
  const minFirst = new Date(minD.getFullYear(), minD.getMonth(), 1);
  const maxFirst = new Date(maxD.getFullYear(), maxD.getMonth(), 1);

  // 桌面双月 [left, left+1];移动三月 [left-1, left, left+1]。
  const monthDates = useMemo(
    () =>
      isMobile
        ? [addMonths(leftMonth, -1), leftMonth, addMonths(leftMonth, 1)]
        : [leftMonth, addMonths(leftMonth, 1)],
    [leftMonth, isMobile]
  );

  const canPrev = leftMonth > minFirst;
  // 桌面:右月到 max 月即止;移动:中间月到 max 月即止。
  const canNext = isMobile ? leftMonth < maxFirst : addMonths(leftMonth, 1) < maxFirst;

  const grids = useMemo(
    () => monthDates.map((m) => buildMonthGrid(m.getFullYear(), m.getMonth(), isMobile)),
    [monthDates, isMobile]
  );

  // 移动竖向轮播(桌面 enabled=false,noop)。
  const {
    clipRef,
    slideRef,
    translate,
    transition: swipeTransition,
    clipHeight,
  } = useVerticalSwipeMonth({
    month: leftMonth,
    onChange: (m) => setLeftMonth(m),
    addMonths,
    enabled: isMobile,
  });

  const handlePrev = useCallback(() => {
    if (canPrev) setLeftMonth((m) => addMonths(m, -1));
  }, [canPrev]);

  const handleNext = useCallback(() => {
    if (canNext) setLeftMonth((m) => addMonths(m, 1));
  }, [canNext]);

  const classify = useCallback(
    (d: Date): string => {
      const cls: string[] = [];
      if (from && isSameDay(d, from)) {
        cls.push('hh-cal__day--start');
        if (!to) cls.push('hh-cal__day--single');
      }
      if (to && isSameDay(d, to)) cls.push('hh-cal__day--end');
      if (from && to && d > from && d < to) cls.push('hh-cal__day--in-range');
      return cls.join(' ');
    },
    [from, to]
  );

  const handlePick = useCallback(
    (d: Date) => {
      if (d < minD || d > maxD) return;
      // 无 from 或两端都有:以 d 为新 from
      if (!from || (from && to)) {
        setFrom(d);
        setTo(undefined);
        return;
      }
      // 有 from 无 to
      if (isSameDay(d, from)) {
        setFrom(undefined);
        setTo(undefined);
        return;
      }
      if (d < from) {
        setFrom(d);
        return;
      }
      // d > from:提交
      setTo(d);
      onChange({ start: from, end: d, nights: differenceInCalendarDays(from, d) });
    },
    [from, to, minD, maxD, onChange]
  );

  const renderMonth = (monthDate: Date, cells: DayCell[], idx: number) => (
    <div className="hh-cal__month" key={idx}>
      <div className="hh-cal__head">
        {isMobile ? (
          <span>{monthLabel(monthDate)}</span>
        ) : (
          <>
            {idx === 0 ? (
              <button
                type="button"
                className="hh-cal__nav"
                onClick={handlePrev}
                disabled={!canPrev}
                aria-label="上个月"
              >
                ‹
              </button>
            ) : (
              <span />
            )}
            <span>{monthLabel(monthDate)}</span>
            {idx === 1 ? (
              <button
                type="button"
                className="hh-cal__nav"
                onClick={handleNext}
                disabled={!canNext}
                aria-label="下个月"
              >
                ›
              </button>
            ) : (
              <span />
            )}
          </>
        )}
      </div>
      <div className="hh-cal__weekdays">
        {WEEKDAYS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="hh-cal__grid">
        {cells.map((cell, i) => {
          if (!cell.inMonth) return <span key={i} className="hh-cal__day hh-cal__day--other" />;
          const disabled = cell.date < minD || cell.date > maxD;
          const cls = classify(cell.date);
          return (
            <button
              key={i}
              type="button"
              className={`hh-cal__day ${cls}`}
              disabled={disabled}
              onClick={() => handlePick(cell.date)}
              title={getWeekdayZh(cell.date)}
            >
              {cell.date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  // ===== 移动:三月竖向轮播(裁剪只露当月 + 整体平移) =====
  if (isMobile) {
    return (
      <div className="hh-cal-root">
        {/* 窄屏 PC(轮播 + 有鼠标)下的浮箭头:位于裁剪窗外、浮于当月标题两侧。纯触摸机型不渲染。 */}
        {hasFinePointer && (
          <>
            <button
              type="button"
              aria-label="上一月"
              onClick={handlePrev}
              disabled={!canPrev}
              className="hh-cal__float-nav hh-cal__float-nav--prev"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="下一月"
              onClick={handleNext}
              disabled={!canNext}
              className="hh-cal__float-nav hh-cal__float-nav--next"
            >
              ›
            </button>
          </>
        )}
        <div ref={clipRef} className="hh-cal-clip" style={{ height: clipHeight || undefined }}>
          <div
            ref={slideRef}
            className="hh-cal-slide"
            style={{
              transform: `translateY(${translate}px)`,
              transition: swipeTransition ? 'transform 0.28s ease-out' : 'none',
            }}
          >
            <div className="hh-cal">
              {monthDates.map((md, idx) => renderMonth(md, grids[idx], idx))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== 桌面:双月并排(原样,零影响) =====
  return (
    <div className="hh-cal">{monthDates.map((md, idx) => renderMonth(md, grids[idx], idx))}</div>
  );
}
